import { Check, ShieldCheck, UserPlus, X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Avatar, Panel } from '@/components/redesign/bits'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/ui/states'
import { PageTitle } from '@/app/layout'
import { useMe } from '@/features/auth/api'
import { errorMessage, isApiError } from '@/lib/api/errors'
import type { Staff, StaffRole } from '@/lib/api/types'
import { formatDate, formatRelative, numberFmt } from '@/lib/format'
import {
  type TicketStats,
  useChangeRole,
  useInviteStaff,
  useRevokeInvitation,
  useStaff,
  useStaffAction,
} from './api'

const roleLabels: Record<StaffRole, string> = { owner: 'Owner', admin: 'Admin', support: 'Support' }

const roleHelp: Record<StaffRole, string> = {
  owner: 'Everything, including the team.',
  admin: 'Also customers’ plans, discounts and app settings.',
  support: 'The support queue, and customers to look up (no changes to plans).',
}

/** What each role can do, as the control panel enforces it. */
const CAN: [string, boolean, boolean, boolean][] = [
  ['Answer tickets, look up customers', true, true, true],
  ['Change plans, discounts and settings', true, true, false],
  ['Open Queues', true, true, false],
  ['Invite staff, change roles, turn off access', true, false, false],
  ['See service limits and spend', true, false, false],
]

/**
 * Who has access to the control panel, as cards (redesign of 2026-10-16). Owners invite people,
 * set roles, turn off access and reset two-step sign-in.
 */
export function TeamPage() {
  const me = useMe()
  const staff = useStaff()
  const revoke = useRevokeInvitation()
  const [inviting, setInviting] = useState(false)
  const owner = me.data?.role === 'owner'
  const fail = (e: unknown) => toast.error(errorMessage(e))

  return (
    <>
      <PageTitle
        title="Team"
        description="Who can use the control panel. Everyone signs in with a password and a code from an authenticator app."
        actions={
          owner && (
            <button type="button" className="btn primary" onClick={() => setInviting((v) => !v)}>
              <UserPlus aria-hidden="true" />
              Invite someone
            </button>
          )
        }
      />
      {owner && inviting && <InvitePanel onDone={() => setInviting(false)} />}

      {staff.isPending ? (
        <Skeleton className="h-48 rounded-[14px]" />
      ) : staff.isError ? (
        <div className="card">
          <ErrorState error={staff.error} onRetry={() => void staff.refetch()} />
        </div>
      ) : (
        <>
          <ul className="cards" aria-label="Staff" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {staff.data.data.map((s) => (
              <StaffCard key={s.id} s={s} you={s.id === me.data?.id} owner={owner} />
            ))}
          </ul>
          {staff.data.ticket_stats && <SupportWork staff={staff.data.data} stats={staff.data.ticket_stats} />}

          {staff.data.invitations.length > 0 && (
            <Panel title="Invitations" sub="Waiting to be accepted">
              <ul aria-label="Invitations" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {staff.data.invitations.map((i) => (
                  <li key={i.id} className="srow">
                    <div>
                      <b>
                        {i.name} · {i.email}
                      </b>
                      <small>
                        {roleLabels[i.role]} · invited by {i.invited_by ?? 'an owner'} · link works until{' '}
                        {formatDate(i.expires_at)}
                      </small>
                    </div>
                    {owner && (
                      <button
                        type="button"
                        className="btn sm ghost danger"
                        disabled={revoke.isPending}
                        onClick={() =>
                          revoke.mutate(i.id, {
                            onSuccess: () => toast.success('Invitation cancelled.'),
                            onError: fail,
                          })
                        }
                      >
                        <X aria-hidden="true" />
                        Revoke
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </Panel>
          )}
        </>
      )}

      <Panel title="What each role can do">
        <div className="tw" style={{ border: 0 }}>
          <table className="t" style={{ minWidth: 520 }}>
            <caption className="sr-only">What each role can do</caption>
            <thead>
              <tr>
                <th>Can</th>
                <th>Owner</th>
                <th>Admin</th>
                <th>Support</th>
              </tr>
            </thead>
            <tbody>
              {CAN.map(([label, ...roles]) => (
                <tr key={label}>
                  <td>{label}</td>
                  {roles.map((yes, i) => (
                    <td key={i}>
                      {yes ? (
                        <span style={{ color: 'var(--ok)' }}>
                          <Check aria-label="Yes" className="size-4" />
                        </span>
                      ) : (
                        <span className="m" aria-label="No">
                          —
                        </span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  )
}

function StaffCard({ s, you, owner }: { s: Staff; you: boolean; owner: boolean }) {
  const act = useStaffAction()
  const role = useChangeRole()
  const fail = (e: unknown) => toast.error(errorMessage(e))
  const run = (action: 'disable' | 'enable' | 'reset-two-factor', done: string) =>
    act.mutate({ id: s.id, action }, { onSuccess: () => toast.success(done), onError: fail })

  return (
    <li className="card dc" aria-label={s.name}>
      <div className="who">
        <Avatar name={s.name} size="lg" me={you} />
        <div>
          <b style={{ fontSize: 15 }}>{s.name}</b>
          <small>
            {roleLabels[s.role]}
            {you && ' · you'}
          </small>
        </div>
        <span style={{ marginLeft: 'auto' }}>
          {s.disabled ? (
            <span className="chip bad">Access off</span>
          ) : s.two_factor ? (
            <span className="chip ok">
              <ShieldCheck aria-hidden="true" className="size-3.5" />
              2-step on
            </span>
          ) : (
            <span className="chip warn">Sets up 2-step at next sign-in</span>
          )}
        </span>
      </div>
      <div className="grid2" style={{ gap: 8, borderTop: '1px solid var(--line)', paddingTop: 12 }}>
        <div>
          <div style={{ fontSize: 12, color: 'var(--subtle)' }}>Email</div>
          <div style={{ fontWeight: 600, fontSize: 13.5, overflowWrap: 'anywhere' }}>{s.email}</div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: 'var(--subtle)' }}>Last signed in</div>
          <div style={{ fontWeight: 600, fontSize: 13.5 }}>
            {s.last_login_at ? formatRelative(s.last_login_at) : 'Never'}
          </div>
        </div>
      </div>
      {owner && !you && (
        <div className="bar">
          <NativeSelect
            aria-label={`${s.name}’s role`}
            value={s.role}
            disabled={role.isPending}
            onChange={(e) => {
              const next = e.target.value as StaffRole
              role.mutate(
                { id: s.id, role: next },
                {
                  onSuccess: () => toast.success(`${s.name} is now ${roleLabels[next].toLowerCase()}.`),
                  onError: fail,
                },
              )
            }}
            className="h-8 w-32 text-xs"
          >
            {(Object.keys(roleLabels) as StaffRole[]).map((r) => (
              <option key={r} value={r}>
                {roleLabels[r]}
              </option>
            ))}
          </NativeSelect>
          <span className="acts push">
            {s.two_factor && (
              <button
                type="button"
                className="btn sm ghost"
                disabled={act.isPending}
                onClick={() => run('reset-two-factor', 'Two-step sign-in reset.')}
              >
                Reset 2-step
              </button>
            )}
            <button
              type="button"
              className={s.disabled ? 'btn sm ghost' : 'btn sm ghost danger'}
              disabled={act.isPending}
              onClick={() =>
                run(
                  s.disabled ? 'enable' : 'disable',
                  s.disabled ? 'Access turned on.' : 'Access turned off.',
                )
              }
            >
              {s.disabled ? 'Turn on access' : 'Turn off access'}
            </button>
          </span>
        </div>
      )}
    </li>
  )
}

function InvitePanel({ onDone }: { onDone: () => void }) {
  const invite = useInviteStaff()
  const [form, setForm] = useState({ name: '', email: '', role: 'support' as StaffRole })
  const [errors, setErrors] = useState<Record<string, string[]>>({})

  return (
    <Panel
      title="Invite someone"
      sub="They get an email to set a password, then set up an authenticator app at their first sign-in."
    >
      <form
        className="grid gap-3 sm:grid-cols-[1fr_1fr_10rem_auto] sm:items-end"
        onSubmit={(e) => {
          e.preventDefault()
          setErrors({})
          invite.mutate(
            { name: form.name.trim(), email: form.email.trim(), role: form.role },
            {
              onSuccess: () => {
                toast.success(`Invitation sent to ${form.email.trim()}.`)
                setForm({ name: '', email: '', role: 'support' })
                onDone()
              },
              onError: (err) =>
                isApiError(err) && err.isValidation ? setErrors(err.errors) : toast.error(errorMessage(err)),
            },
          )
        }}
      >
        <Field label="Name" htmlFor="invite-name" required error={errors.name?.[0]}>
          <Input
            id="invite-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>
        <Field label="Work email" htmlFor="invite-email" required error={errors.email?.[0]}>
          <Input
            id="invite-email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="name@arvaseo.com"
          />
        </Field>
        <Field label="Role" htmlFor="invite-role">
          <NativeSelect
            id="invite-role"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value as StaffRole })}
          >
            {(Object.keys(roleLabels) as StaffRole[]).map((r) => (
              <option key={r} value={r}>
                {roleLabels[r]}
              </option>
            ))}
          </NativeSelect>
        </Field>
        <button
          type="submit"
          className="btn primary"
          disabled={invite.isPending || form.name.trim() === '' || form.email.trim() === ''}
        >
          Send invitation
        </button>
      </form>
      <p style={{ fontSize: 12.5, color: 'var(--subtle)' }}>
        {roleLabels[form.role]}: {roleHelp[form.role]}
      </p>
    </Panel>
  )
}

/** "45 min", "2 h 10 min", "1 d 3 h": how long customers waited. */
function waited(minutes: number | null): string {
  if (minutes === null) return '—'
  if (minutes < 60) return `${minutes} min`
  if (minutes < 24 * 60) {
    const m = minutes % 60
    return `${Math.floor(minutes / 60)} h${m ? ` ${m} min` : ''}`
  }
  const h = Math.floor((minutes % (24 * 60)) / 60)
  return `${Math.floor(minutes / (24 * 60))} d${h ? ` ${h} h` : ''}`
}

/** Who answered tickets, how quickly, and what's still on their plate (owner, 2026-10-20). */
function SupportWork({ staff, stats }: { staff: Staff[]; stats: TicketStats }) {
  const rows = staff.flatMap((s) => {
    const r = stats.by_staff[s.id]
    return r ? [{ s, r }] : []
  })
  return (
    <Panel
      title={`Support, last ${stats.days} days`}
      sub="Replies written, tickets replied on, how long customers waited for a first reply, and assigned tickets"
    >
      {rows.length === 0 ? (
        <p className="empty-line">No replies in the last {stats.days} days.</p>
      ) : (
        <div className="tw" style={{ border: 0 }}>
          <table className="t" style={{ minWidth: 620 }}>
            <caption className="sr-only">Support work per person</caption>
            <thead>
              <tr>
                <th>Person</th>
                <th className="r">Replies</th>
                <th className="r">Tickets</th>
                <th className="r">First reply, median</th>
                <th className="r">Assigned, open</th>
                <th className="r">Resolved</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ s, r }) => {
                return (
                  <tr key={s.id}>
                    <td>
                      <b>{s.name}</b>
                      <small>{roleLabels[s.role]}</small>
                    </td>
                    <td className="r num">{numberFmt.format(r.replies)}</td>
                    <td className="r num">{numberFmt.format(r.tickets)}</td>
                    <td className="r num">
                      {waited(r.median_first_reply_minutes)}
                      {r.first_replies > 0 && (
                        <small>
                          on {numberFmt.format(r.first_replies)}{' '}
                          {r.first_replies === 1 ? 'ticket' : 'tickets'}
                        </small>
                      )}
                    </td>
                    <td className="r num">{numberFmt.format(r.open_assigned)}</td>
                    <td className="r num">{numberFmt.format(r.resolved)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  )
}
